import { useEffect, useState, useRef } from "react";
import { useNeuroaBridge } from '../../../context/NeuroaBridgeContext';

/**
 * Hook to manage Neuroa AI interactions in the dashboard.
 * Handles welcome greetings, sidebar transition responses, and logo animations.
 * 
 * @param {Object} dashboardNeuroaLogoRef - Ref to the NeuroaLogo component to trigger animations.
 * @returns {Object} { handleNeuroaClick, isSidebarOpen, isNeuroaOpen }
 */
export const useNeuroaDashboard = (dashboardNeuroaLogoRef) => {
  const { 
    openNeuroa, 
    isSidebarOpen, 
    isNeuroaOpen,
    triggerGreeting
  } = useNeuroaBridge();

  const [hasGreetedOnTransition, setHasGreetedOnTransition] = useState(false);
  const hasAnyGreetingTriggered = useRef(false);
  const isInitialSidebarRender = useRef(true);

  // Initial welcome greeting
  useEffect(() => {
    if (isNeuroaOpen || hasAnyGreetingTriggered.current) return;
    
    const welcomeTimeout = setTimeout(() => {
      // Re-check conditions after timeout
      if (isNeuroaOpen || hasAnyGreetingTriggered.current) return;
      
      if (dashboardNeuroaLogoRef.current) {
        dashboardNeuroaLogoRef.current.wink();
      }
      
      hasAnyGreetingTriggered.current = true;
      triggerGreeting("Conversa con Neuroa");
    }, 1500);

    return () => clearTimeout(welcomeTimeout);
  }, [isNeuroaOpen, triggerGreeting, dashboardNeuroaLogoRef]);

  // Sidebar transition greeting
  useEffect(() => {
    // Skip initial render to avoid double greeting with the welcomeTimeout
    if (isInitialSidebarRender.current) {
      isInitialSidebarRender.current = false;
      return;
    }

    if (dashboardNeuroaLogoRef.current) {
      dashboardNeuroaLogoRef.current.wink();
    }

    // Greet when sidebar closes if we haven't greeted for a transition yet
    if (!isSidebarOpen && !hasGreetedOnTransition && !isNeuroaOpen) {
      setHasGreetedOnTransition(true);
      hasAnyGreetingTriggered.current = true;
      triggerGreeting("¡Hey aquí sigo!");
    }
  }, [isSidebarOpen, isNeuroaOpen, hasGreetedOnTransition, triggerGreeting, dashboardNeuroaLogoRef]);

  /**
   * Handles clicks on the Neuroa logo/button.
   * Triggers a wink animation before opening the Neuroa panel.
   */
  const handleNeuroaClick = () => {
    if (dashboardNeuroaLogoRef.current) {
      dashboardNeuroaLogoRef.current.wink();
    }
    // Small delay to let the animation start
    setTimeout(() => openNeuroa(), 300);
  };

  return { handleNeuroaClick, isSidebarOpen, isNeuroaOpen };
};
